import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../database/config';
import { slugify, getPostSlug } from '../../utils/slugify';
import SEO from '../SEO/index';

const BASE_URL = 'https://www.getbentobox.in';

// Category colors
const categoryColors = {
    "Health": "bg-green-100 text-green-700",
    "Lifestyle": "bg-blue-100 text-blue-700",
    "Food Safety": "bg-red-100 text-red-700",
    "Environment": "bg-emerald-100 text-emerald-700",
    "Recipes": "bg-orange-100 text-orange-700",
    "Tips": "bg-purple-100 text-purple-700",
    "Stories": "bg-pink-100 text-pink-700",
    "Food": "bg-amber-100 text-amber-700",
    "Technology": "bg-indigo-100 text-indigo-700",
    "default": "bg-orange-100 text-orange-700"
};

// Social Icons
const FacebookIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
    </svg>
);

const TwitterIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

const InstagramIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
);

const YouTubeIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
);

const BlogCard = ({ post }) => {
    const categoryClass = categoryColors[post.type] || categoryColors["default"];
    const postSlug = getPostSlug(post);

    return (
        <Link to={`/blog/${postSlug}`} className="block">
            <article className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group">
                {/* Image */}
                <div className="relative overflow-hidden">
                    <img
                        src={post.img}
                        alt={post.heading}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                </div>

                {/* Content */}
                <div className="p-5">
                    {/* Category */}
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${categoryClass}`}>
                        {post.type}
                    </span>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-orange-500 transition-colors">
                        {post.heading}
                    </h3>

                    {/* Author & Date */}
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden">
                            {post.authorImg ? (
                                <img src={post.authorImg} alt={post.author} className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-orange-500 text-sm font-semibold">{post.author?.charAt(0) || 'A'}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span className="font-medium text-gray-700">{post.author}</span>
                            <span>•</span>
                            <span>{post.date}</span>
                        </div>
                    </div>
                </div>
            </article>
        </Link>
    );
};

const AuthorPageDetail = () => {
    const { authorId } = useParams(); // This is now the author name slug (e.g., "john-doe")
    const [cardData, setCardData] = useState(null);
    const [authorData, setAuthorData] = useState(null);
    const [allPosts, setAllPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAuthorData = async () => {
            try {
                // Fetch all blog cards to find the one matching the author slug
                const blogsCollection = collection(db, 'Blogs');
                const blogsSnapshot = await getDocs(blogsCollection);

                let foundCard = null;
                let foundCardId = null;
                const blogPosts = [];

                blogsSnapshot.forEach((docSnap) => {
                    const docId = docSnap.id;
                    if (docId.startsWith('card')) {
                        const data = docSnap.data();
                        const authorSlug = slugify(data.author);

                        // Match by author slug
                        if (authorSlug === authorId && !foundCard) {
                            foundCard = data;
                            foundCardId = docId;
                        }

                        // Collect all posts for Latest Posts section
                        blogPosts.push({
                            id: docId,
                            heading: data.heading || '',
                            author: data.author || 'Anonymous',
                            authorImg: data.authorImg || '',
                            date: data.date || '',
                            img: data.img || '',
                            type: data.type || 'General',
                            slug: data.slug || ''
                        });
                    }
                });

                if (foundCard && foundCardId) {
                    setCardData(foundCard);

                    // Fetch author page content using the actual card ID
                    const authorRef = doc(db, 'authorPage', `${foundCardId}AuthorPage`);
                    const authorSnap = await getDoc(authorRef);

                    if (authorSnap.exists()) {
                        setAuthorData(authorSnap.data());
                    }
                } else {
                    setError('Author not found');
                    setLoading(false);
                    return;
                }

                // Sort cards by their number
                blogPosts.sort((a, b) => {
                    const numA = parseInt(a.id.replace('card', '')) || 0;
                    const numB = parseInt(b.id.replace('card', '')) || 0;
                    return numA - numB;
                });

                setAllPosts(blogPosts);
            } catch (err) {
                console.error('Error fetching author data:', err);
                setError('Failed to load author page');
            } finally {
                setLoading(false);
            }
        };

        if (authorId) {
            fetchAuthorData();
        }
    }, [authorId]);

    // SEO values derived from loaded data
    const authorCanonical = `${BASE_URL}/author/${authorId}`;
    const authorSeoTitle = cardData?.author
        ? `${cardData.author} | BentoBox Blog Author`
        : 'Author | BentoBox Blog';
    const authorSeoDescription = authorData?.aboutMe
        ? authorData.aboutMe.substring(0, 160)
        : `Read articles by ${cardData?.author || 'our author'} on home-cooked food, tiffin services, and healthy eating in Gurgaon.`;
    const authorOgImage = cardData?.authorImg || `${BASE_URL}/og/default.jpg`;

    // Schema.org Person structured data
    const personSchema = cardData ? {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: cardData.author,
        url: authorCanonical,
        image: cardData.authorImg || undefined,
        description: authorData?.aboutMe || undefined,
        jobTitle: authorData?.profession || undefined,
        worksFor: {
            '@type': 'Organization',
            name: 'BentoBox',
            url: BASE_URL,
        },
    } : null;

    if (loading) {
        return (
            <>
                <SEO
                    pageName="author"
                    customTitle="Loading... | BentoBox Blog"
                    customCanonical={authorCanonical}
                />
                <section className="bg-white min-h-screen">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pt-12 md:pb-24 flex items-center justify-center">
                        <div className="animate-pulse text-gray-500 text-lg">Loading author page...</div>
                    </div>
                </section>
            </>
        );
    }


    if (error || !cardData) {
        return (
            <>
                <SEO
                    pageName="blog"
                    customTitle="Author Not Found | BentoBox Blog"
                    customCanonical={`${BASE_URL}/blog`}
                    indexPage={false}
                />
                <section className="bg-white min-h-screen">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pt-12 md:pb-24 text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">{error || 'Author not found'}</h1>
                        <Link to="/blog" className="text-orange-500 hover:text-orange-600 font-medium">
                            ← Back to Blog
                        </Link>
                    </div>
                </section>
            </>
        );
    }

    return (
        <>
        <SEO
            pageName="author"
            customTitle={authorSeoTitle}
            customDescription={authorSeoDescription}
            customCanonical={authorCanonical}
            customOgImage={authorOgImage}
            customStructuredData={personSchema}
        />
        <section className="!pt-28 md:!pt-32 bg-white min-h-screen">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 md:pt-10 md:pb-16">
                {/* Author Header Card */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-12">
                    <div className="text-center max-w-2xl mx-auto">
                        {/* Author Avatar */}
                        <div className="mb-4 flex justify-center">
                            <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden border-4 border-orange-200">
                                {cardData.authorImg ? (
                                    <img src={cardData.authorImg} alt={cardData.author} className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-orange-500 text-2xl font-bold">{cardData.author?.charAt(0) || 'A'}</span>
                                )}
                            </div>
                        </div>

                        {/* Author Name & Profession */}
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
                            {cardData.author}
                        </h1>
                        {authorData?.profession && (
                            <p className="text-gray-500 mb-4">{authorData.profession}</p>
                        )}

                        {/* About Me */}
                        {authorData?.aboutMe && (
                            <p className="text-gray-600 leading-relaxed mb-6">
                                {authorData.aboutMe}
                            </p>
                        )}

                        {/* Social Links */}
                        <div className="flex items-center justify-center gap-3">
                            {authorData?.facebookLink && (
                                <a
                                    href={authorData.facebookLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                                >
                                    <FacebookIcon />
                                </a>
                            )}
                            {authorData?.twitterLink && (
                                <a
                                    href={authorData.twitterLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center text-white hover:bg-sky-600 transition-colors"
                                >
                                    <TwitterIcon />
                                </a>
                            )}
                            {authorData?.instagramLink && (
                                <a
                                    href={authorData.instagramLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                                >
                                    <InstagramIcon />
                                </a>
                            )}
                            {authorData?.youtubeLink && (
                                <a
                                    href={authorData.youtubeLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white hover:bg-red-700 transition-colors"
                                >
                                    <YouTubeIcon />
                                </a>
                            )}
                            {/* Show default social icons if no links provided */}
                            {!authorData?.facebookLink && !authorData?.twitterLink && !authorData?.instagramLink && !authorData?.youtubeLink && (
                                <>
                                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white">
                                        <FacebookIcon />
                                    </div>
                                    <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center text-white">
                                        <TwitterIcon />
                                    </div>
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center text-white">
                                        <InstagramIcon />
                                    </div>
                                    <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white">
                                        <YouTubeIcon />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Latest Posts Section */}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-8">Latest Post</h2>

                    {allPosts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {allPosts.map((post) => (
                                <BlogCard key={post.id} post={post} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            No blog posts available yet.
                        </div>
                    )}
                </div>

                {/* Back Link */}
                <div className="mt-12 text-center">
                    <Link to="/blog" className="text-orange-500 hover:text-orange-600 font-medium inline-flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                        </svg>
                        Back to Blog
                    </Link>
                </div>
            </div>
        </section>
        </>
    );
};

export default AuthorPageDetail;
